import {
  CURRENT_YEAR_SOB_PLAN_FIELD_BY_QUARTER,
  DEFAULT_CY_QUARTER_POLICIES,
  DEFAULT_PY_QUARTER_POLICIES,
  PREVIOUS_YEAR_SOB_PLAN_FIELD_BY_QUARTER,
  SOB_QUARTER_KEYS,
} from '../model/constants'
import type { HierarchicalFinData, SobQuarterKey, SobQuarterPolicies } from '../model/types'

type SobActualRequiredQuarter = {
  fQuarter: number
  sobState: number
}

type FiscalPeriod = {
  fYear: number
  fQuarter: number
}

const hasPlanValue = (value: HierarchicalFinData[keyof HierarchicalFinData]) =>
  typeof value === 'number' && Number.isFinite(value) && value !== 0

export const buildPreviousYearQuarterPolicies = (
  rows: HierarchicalFinData[]
): SobQuarterPolicies => {
  if (!rows.length) {
    return DEFAULT_PY_QUARTER_POLICIES
  }

  return SOB_QUARTER_KEYS.reduce<SobQuarterPolicies>(
    (policies, quarterKey) => {
      const planField = PREVIOUS_YEAR_SOB_PLAN_FIELD_BY_QUARTER[quarterKey]
      const hasMandatoryPlan = rows.some(
        (row) => row.SOBRequired !== 0 && hasPlanValue(row[planField])
      )

      policies[quarterKey] = hasMandatoryPlan ? 'required' : 'optional'

      return policies
    },
    { ...DEFAULT_PY_QUARTER_POLICIES }
  )
}

const isFutureQuarter = (fYear: number, fQuarter: number, actualsPeriod: FiscalPeriod | null) =>
  actualsPeriod !== null &&
  (fYear > actualsPeriod.fYear ||
    (fYear === actualsPeriod.fYear && fQuarter > actualsPeriod.fQuarter))

/** Quarters after the actuals period are never required, whatever the JBP SOB settings say */
export const buildCurrentYearQuarterPolicies = ({
  hasUsableSobActualSettings,
  sobActualRequiredQuarters,
  currentFiscalYear,
  actualsPeriod,
}: {
  hasUsableSobActualSettings: boolean
  sobActualRequiredQuarters: readonly SobActualRequiredQuarter[]
  currentFiscalYear: number
  actualsPeriod: FiscalPeriod | null
}): SobQuarterPolicies => {
  if (!hasUsableSobActualSettings) {
    return DEFAULT_CY_QUARTER_POLICIES
  }

  const policies = { ...DEFAULT_CY_QUARTER_POLICIES }

  sobActualRequiredQuarters.forEach(({ fQuarter, sobState }) => {
    const quarterKey = `q${fQuarter}` as SobQuarterKey

    if (sobState === 1) {
      policies[quarterKey] = isFutureQuarter(currentFiscalYear, fQuarter, actualsPeriod)
        ? 'optional'
        : 'required'
      return
    }

    if (sobState === -1) {
      policies[quarterKey] = 'disabled'
    }
  })

  return policies
}

/** Hierarchy nodes without a current-year SOB plan are never required, like previous-year ones */
export const buildEntryCurrentYearQuarterPolicies = ({
  rows,
  currentYearQuarterPolicies,
}: {
  rows: HierarchicalFinData[]
  currentYearQuarterPolicies: SobQuarterPolicies
}): SobQuarterPolicies =>
  SOB_QUARTER_KEYS.reduce<SobQuarterPolicies>(
    (policies, quarterKey) => {
      if (policies[quarterKey] !== 'required') {
        return policies
      }

      const planField = CURRENT_YEAR_SOB_PLAN_FIELD_BY_QUARTER[quarterKey]
      const hasPlan = rows.some((row) => hasPlanValue(row[planField]))

      if (!hasPlan) {
        policies[quarterKey] = 'optional'
      }

      return policies
    },
    { ...currentYearQuarterPolicies }
  )
